import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ShieldOff, Mail, MailCheck, Search, UserPlus, X, StickyNote } from 'lucide-react';
import { api, formatDate, formatDateTime } from '../../lib/api';
import {
  useScrollShadow, STICKY_CELL, STICKY_HEAD_BG, STICKY_CELL_BG, STICKY_SHADOW,
} from '../../lib/sticky-column';

export default function AdminUsers() {
  const navigate = useNavigate();
  const [tableRef, shadowed] = useScrollShadow();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = () => {
    setLoading(true);
    api('/api/admin/users')
      .then((d) => setUsers(d.users || []))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? users.filter((u) =>
        u.email.toLowerCase().includes(term) ||
        u.full_name.toLowerCase().includes(term)
      )
    : users;

  const toggleAdmin = async (user) => {
    const next = !user.is_admin;
    if (next && !confirm(`Promote ${user.email} to admin?`)) return;
    if (!next && !confirm(`Remove admin from ${user.email}?`)) return;
    setBusyId(user.id);
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: { is_admin: next } });
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Users <span className="text-slate-400 text-base font-normal">({users.length})</span></h1>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark transition"
          >
            <UserPlus size={14} /> Add User
          </button>
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-72"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No users match.</div>
        ) : (
          <div ref={tableRef} className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Name</th>
                <th className="text-left px-3 py-3 font-semibold">Email</th>
                <th className="text-left px-3 py-3 font-semibold hidden md:table-cell">Role</th>
                <th className="text-left px-3 py-3 font-semibold hidden lg:table-cell">Joined</th>
                <th className="text-left px-3 py-3 font-semibold hidden lg:table-cell">Last login</th>
                <th className={`text-right px-5 py-3 font-semibold ${STICKY_CELL} ${STICKY_HEAD_BG} ${shadowed ? STICKY_SHADOW : ''}`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} className="group hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/users/${u.id}`)}>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                        {u.full_name.charAt(0).toUpperCase()}
                      </div>
                      {u.full_name}
                      {u.has_admin_notes && (
                        <StickyNote
                          size={13}
                          className="text-amber-500 shrink-0"
                          title="Has internal admin notes"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-700">
                    <div className="flex items-center gap-1.5">
                      {u.email_verified ? (
                        <MailCheck size={12} className="text-emerald-500" title="Verified" />
                      ) : (
                        <Mail size={12} className="text-slate-400" title="Unverified" />
                      )}
                      {u.email}
                    </div>
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell">
                    {u.is_admin ? (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-violet-100 text-violet-700">Admin</span>
                    ) : (
                      <span className="text-xs text-slate-500">Learner</span>
                    )}
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell text-xs text-slate-500">{formatDate(u.created_at)}</td>
                  <td className="px-3 py-3 hidden lg:table-cell text-xs text-slate-500">{u.last_login_at ? formatDateTime(u.last_login_at) : '—'}</td>
                  <td className={`px-5 py-3 text-right ${STICKY_CELL} ${STICKY_CELL_BG} ${shadowed ? STICKY_SHADOW : ''}`}>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleAdmin(u); }}
                      disabled={busyId === u.id}
                      title={u.is_admin ? 'Remove admin' : 'Promote to admin'}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ${
                        u.is_admin
                          ? 'text-rose-600 hover:bg-rose-50'
                          : 'text-violet-600 hover:bg-violet-50'
                      } disabled:opacity-30`}
                    >
                      {u.is_admin ? <><ShieldOff size={12} /> Demote</> : <><Shield size={12} /> Promote</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onCreated={refresh} />}
    </div>
  );
}

function AddUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ full_name: '', email: '', password: '', is_admin: false });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api('/api/admin/users', { method: 'POST', body: form });
      if (res.error) {
        setError(res.error === 'email_exists' ? 'A user with that email already exists.' : res.error === 'password_too_short' ? 'Password must be at least 8 characters.' : res.error);
        return;
      }
      onCreated();
      onClose();
    } catch {
      setError('Could not create user.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold">Add User</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Full Name</label>
            <input required value={form.full_name} onChange={set('full_name')} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
            <input type="email" required value={form.email} onChange={set('email')} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Password</label>
            <input type="text" required minLength={8} value={form.password} onChange={set('password')} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_admin} onChange={set('is_admin')} className="rounded border-slate-300" />
            <span className="text-slate-700">Admin privileges</span>
          </label>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={busy} className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg disabled:opacity-50">
              {busy ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
